import { DateTime } from "luxon";
import { cloneDeep } from "lodash-es";

import LocalForagePlugin, { syncState, tags } from "@/plugins/LocalForagePlugin";
import { tagsSyncKey } from "@/types/products/product-definitions";

import { goFetch } from "@/utils/network-utils";
import type { PersistedQueryPayload } from "@/types/api/fetch";

import type { TagApiResponse, Tags } from "@/types/api/content-models";
import type { Tag } from "@/types/entities";

import { consoleLoggerFactory, getDefaultLoggerOptions, LogLevels } from "@/types/console-logger";

const logger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

export async function refreshTagsFromServer(): Promise< Tag[] > {
    let allTags: Tag[] = [];

    const t0: number = performance.now();

    let page: number = 0;
    const pageSize: number = 100;

    let tagPayload: Tag[] | undefined = [];

    do {
        const response =
            await goFetch< Tags, PersistedQueryPayload >( {
                endpoint: 'content/graphql',
                payload: {
                    id: 'b9f293cd133e29ae59c3df8a3da55082',
                    variables: {
                        skip: page,
                        limit: pageSize
                    }
                }
            } );

        if ( response?.data?.tagCatalog?.tags ) {
            tagPayload = [ ...( response.data.tagCatalog.tags
                    .map( ( t: TagApiResponse ): Tag => ( {
                        id: t.id,
                        publishedAt: t.published_at,
                        name: t.name,
                        slug: t.slug
                    } ) )
            ) ];

            const t1: number = performance.now();
            logger.info( `Fetching & mapping ${ tagPayload.length } tags items took ${ t1 - t0 } ms` );

            allTags = [ ...tagPayload, ...allTags ];
        }

        // update the page #
        page++;
    } while ( (tagPayload?.length ?? 0) >= pageSize );

    return allTags;
}

export async function loadTagMap( tagsMap: Map< string, Tag > ): Promise< boolean > {
    let updated = false;
    try {
        const t0: number = performance.now();

        if ( typeof tags === 'undefined' || typeof syncState === 'undefined' ) {
            // Wait for the driver to settle
            LocalForagePlugin.initialize();
        }

        await syncState.ready();
        // Check for the next refresh interval
        const syncThresholdInMillis = await syncState.getItem< number >( tagsSyncKey );
        if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() ) {

            const nextSyncThresholdInMillis = DateTime.utc().plus( { hours: 4 } ).toMillis();
            await syncState.setItem( tagsSyncKey, nextSyncThresholdInMillis );

            // Retrieve the latest collection of tags from the content service
            const tagsReceived: Tag[] = await refreshTagsFromServer();

            if(tagsReceived.length > 0)
            {
                updated = true;
                // Store the items recently retrieved
                await tags.ready();
                for ( const tag of tagsReceived ) {
                    await tags.setItem( tag.id, cloneDeep( tag ) );
                }
                const t1: number = performance.now();
                logger.info( `[ sync-worker ] Refreshed ${ tagsMap.size } tags from server in ${ t1 - t0 } ms` );
            }
            else
            {
                const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 15 } ).toMillis();
                await syncState.setItem( tagsSyncKey, nextSyncThresholdInMillis );
            }
        }

        // Make sure map is empty
        tagsMap.clear();

        // Load whatever is in the local cache
        await tags.ready();
        // Iterate over all elements within IDB
        await tags.iterate( ( tag: Tag ) => {
            tagsMap.set( tag.id, tag );
            return;
        } );
    } catch ( err: any ) {
        logger.error( JSON.stringify( err.message ) );
    }

    return updated;
}
